import React from "react";
import {
  Building2, CheckCircle2, Clock, FileText, Truck, BellRing,
  ShieldCheck, Wrench, AlertTriangle, ArrowRight, Phone, Sparkles,
} from "lucide-react";

const BUSINESS_BG =
  "https://images.unsplash.com/photo-1486006920555-c77dcf18193c?crop=entropy&cs=srgb&fm=jpg&ixid=&ixlib=rb-4.1.0&q=85&w=1400";

const BENEFITS = [
  { icon: Clock, t: "Úspora času pro majitele a zaměstnance" },
  { icon: FileText, t: "Méně administrativy okolo STK, emisí a přepisů" },
  { icon: Truck, t: "Vyzvednutí a vrácení vozidla po domluvě" },
  { icon: BellRing, t: "Přehlednější hlídání termínů STK a emisí" },
  { icon: ShieldCheck, t: "Pomoc při evidenčních kontrolách a přepisech" },
  { icon: Building2, t: "Možnost řešení více vozidel najednou" },
  { icon: Wrench, t: "Servisní příprava přes doporučeného partnera" },
  { icon: AlertTriangle, t: "Pomoc i v případě, že vozidlo neprojde STK" },
  { icon: Sparkles, t: "Individuální domluva podle počtu vozidel a potřeb" },
  { icon: CheckCircle2, t: "Profesionální komunikace a jasný další postup" },
];

const SUITABLE_FOR = [
  "Živnostníci",
  "Malé a střední firmy",
  "Firmy s dodávkami",
  "Správci vozového parku",
  "Podnikatelé, kteří nechtějí ztrácet čas",
  "Klienti, kteří potřebují fakturaci a přehledný postup",
];

export default function ForBusiness() {
  return (
    <section
      id="pro-firmy"
      data-testid="for-business-section"
      className="relative bg-[#0A0D17] text-white py-20 lg:py-24 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* HERO BLOCK */}
        <div className="relative overflow-hidden rounded-3xl border border-amber-300/15 bg-gradient-to-br from-[#0E1730] via-[#0A0D17] to-[#0A0D17]">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 lg:p-12 xl:p-14">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-300/5 px-3 py-1 text-xs uppercase tracking-[0.2em] text-amber-300">
                <Building2 className="h-3.5 w-3.5" />
                Pro firmy a firemní vozidla
              </div>
              <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
                STK a auto služby pro firmy
              </h2>
              <p className="font-body mt-3 text-amber-200/85 text-base">
                Šetříme čas podnikatelům, firmám i správcům vozového parku.
              </p>
              <p className="font-body mt-5 text-[15px] text-slate-300 leading-relaxed max-w-xl">
                Pro firmy, živnostníky a menší vozové parky nabízíme pomoc
                s organizací STK, emisí, evidenčních kontrol, přepisů vozidel,
                administrativy a servisní přípravy. Po domluvě můžeme vozidlo
                vyzvednout na adrese, zajistit potřebný postup a po vyřízení
                jej dovézt zpět.
              </p>
              <p className="font-body mt-4 text-[15px] text-slate-300 leading-relaxed max-w-xl">
                Pokud vozidlo při kontrole neprojde nebo je potřeba ho před
                STK opravit, pomůžeme domluvit další postup a podle situace
                doporučíme nebo zprostředkujeme servisní řešení přes
                partnerský autoservis. Naším cílem je celý proces co nejvíce
                zjednodušit.
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href="/objednavka?klient=firma"
                  data-testid="business-cta-order"
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 px-5 py-2.5 text-sm font-semibold text-slate-900 shadow-[0_10px_24px_-8px_rgba(212,175,55,0.5)] hover:-translate-y-0.5 transition-transform"
                >
                  Zadat firemní vozidlo
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#kontakt"
                  data-testid="business-cta-meeting"
                  className="inline-flex items-center gap-2 rounded-xl border border-amber-300/30 bg-amber-300/10 px-5 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-300/20 transition-all"
                >
                  Domluvit firemní spolupráci
                </a>
                <a
                  href="tel:+420775659129"
                  data-testid="business-cta-call"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/[0.08] transition-all"
                >
                  <Phone className="h-4 w-4" />
                  Nechat si zavolat
                </a>
              </div>
            </div>

            <div className="relative min-h-[260px] lg:min-h-full">
              <img
                src={BUSINESS_BG}
                alt="Vozový park firmy ve Znojmě — služby STK Komfort"
                className="absolute inset-0 h-full w-full object-cover opacity-60"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D17] via-[#0A0D17]/40 to-transparent" />
            </div>
          </div>
        </div>

        {/* BENEFITS GRID */}
        <div className="mt-10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold">
            Hlavní výhody pro firmy
          </div>
          <h3 className="font-heading mt-2 text-2xl sm:text-3xl font-bold text-white">
            Vy řešíte svůj čas. My pomůžeme vyřešit vozidlo.
          </h3>
          <ul
            data-testid="business-benefits"
            className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <li
                  key={b.t}
                  className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.04] transition-colors"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-300/10 border border-amber-300/25 text-amber-300">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm text-slate-200 leading-relaxed pt-1.5">
                    {b.t}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-5 text-xs text-slate-500 italic max-w-3xl leading-relaxed">
            Dostupnost vyzvednutí, servisní přípravy a konkrétní termíny se
            vždy potvrzují individuálně podle kapacity, lokality a stavu
            vozidla.
          </p>
        </div>

        {/* WHEN VEHICLE FAILS STK */}
        <div className="mt-10 grid lg:grid-cols-5 gap-6">
          <div
            data-testid="business-fail-block"
            className="lg:col-span-3 rounded-3xl border border-amber-300/20 bg-gradient-to-br from-[#1A1208] via-[#0E1322] to-[#0A0D17] p-7 sm:p-9"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" />
              Když vozidlo neprojde STK
            </div>
            <h3 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-white leading-tight">
              Co když vozidlo neprojde STK?
            </h3>
            <p className="mt-5 text-[15px] text-slate-300 leading-relaxed">
              Pokud vozidlo při technické kontrole neprojde, nemusíte
              složitě hledat další řešení. Pomůžeme vám vyhodnotit další
              postup a podle situace doporučíme servisní přípravu nebo
              opravu přes partnerský autoservis. Po opravě je možné
              domluvit další kroky tak, aby byl celý proces pro klienta
              co nejjednodušší.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {[
                "Vyhodnotíme další postup",
                "Doporučíme servisní řešení",
                "Podle stavu vozidla",
                "Podle dostupnosti partnera",
                "Po jasné domluvě s klientem",
                "Bez slibů průchodu STK",
              ].map((p) => (
                <div
                  key={p}
                  className="flex items-start gap-2 text-sm text-slate-200"
                >
                  <CheckCircle2 className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* O nic se nestaráte */}
          <div
            data-testid="business-care-block"
            className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/[0.02] p-7 sm:p-9"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold">
              <Sparkles className="h-3.5 w-3.5" />
              O nic se nestaráte
            </div>
            <h3 className="font-heading mt-3 text-xl font-bold text-white leading-tight">
              O nic se zbytečně nestaráte. Od toho jsme tu my.
            </h3>
            <p className="mt-4 text-sm text-slate-300 leading-relaxed">
              Naším cílem je převzít za klienta maximum starostí kolem
              vozidla. Od první domluvy přes převzetí vozidla, organizaci
              STK nebo emisí, komunikaci, případnou servisní přípravu
              až po vrácení vozidla zpět. Vše probíhá po jasné domluvě,
              férově a profesionálně.
            </p>
            <p className="mt-5 pt-5 border-t border-white/5 text-xs italic text-amber-200/80 leading-relaxed">
              „Vozidlo, termíny, administrativa, servisní postup. Vše
              pomůžeme sladit za vás.&rdquo;
            </p>
          </div>
        </div>

        {/* TRUST BAND */}
        <div className="mt-10 rounded-3xl border border-amber-300/15 bg-gradient-to-br from-[#0E1730] to-[#0A0D17] p-7 sm:p-9">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold">
                Důvěryhodnost pro firmy
              </div>
              <h3 className="font-heading mt-3 text-xl sm:text-2xl font-bold text-white leading-snug">
                Čas, spolehlivost a jasný proces
              </h3>
              <p className="mt-4 text-[15px] text-slate-300 leading-relaxed">
                Pro firemní klienty je důležitý čas, spolehlivost a jasný
                proces. Proto nastavujeme spolupráci tak, aby bylo možné
                řešit jednotlivá vozidla i opakované termíny přehledně,
                profesionálně a s minimálním zatížením klienta.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300/85 font-semibold mb-3">
                Vhodné pro
              </div>
              <ul className="space-y-2">
                {SUITABLE_FOR.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-2 text-sm text-slate-200"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-300" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
